export type HelpTopic = {
  title: string;
  summary: string;
  steps?: string[];
  tips?: string[];
};

export const ROLE_INTRO: Record<"admin" | "teacher" | "student", { subtitle: string; desc: string }> = {
  admin: {
    subtitle: "Quản lý toàn diện",
    desc: "Dashboard, quản lý học viên, lớp học, tài chính, báo cáo — tất cả trong tầm tay.",
  },
  teacher: {
    subtitle: "Tập trung dạy học",
    desc: "Lịch dạy, điểm danh, giao bài tập, theo dõi tiến độ học viên.",
  },
  student: {
    subtitle: "Học tập hiệu quả",
    desc: "Xem lịch học, bài tập, điểm danh, thông báo từ trung tâm.",
  },
};

export const HELP_CONTENT: Record<string, HelpTopic> = {
  // ===== ADMIN =====
  "/admin/students": {
    title: "Quản lý người dùng",
    summary: "Tạo tài khoản cho giáo viên và học sinh để họ tự đăng nhập vào cổng riêng.",
    steps: [
      "Bấm 'Tạo tài khoản' ở góc trên",
      "Chọn vai trò: Giáo viên hoặc Học sinh",
      "Điền tên, để trống email/SĐT nếu chưa có — hệ thống tự tạo tài khoản đăng nhập",
      "Với học sinh: chọn lớp để thêm vào ngay (có thể thêm sau ở trang Lớp học)",
    ],
    tips: ["Tài khoản học sinh/giáo viên dùng để họ tự đăng nhập vào cổng riêng của mình"],
  },
  "/admin/classes": {
    title: "Quản lý lớp học",
    summary: "Tạo lớp, xếp lịch, phân giáo viên — tất cả trong vài click.",
    steps: [
      "Bấm 'Tạo lớp' và điền tên lớp, môn học",
      "Phân giáo viên phụ trách (chọn từ danh sách đã tạo ở trang Người dùng)",
      "Vào lớp vừa tạo → thêm buổi học (ngày, giờ bắt đầu, giờ kết thúc, phòng)",
      "Thêm học sinh vào lớp từ danh sách đã có",
    ],
    tips: ["Quản lý nhiều cơ sở, nhiều ca học trong cùng một trung tâm."],
  },
  "/admin/my-schedule": {
    title: "Lịch dạy của bạn",
    summary: "Dành cho gói Gia sư khi bạn vừa quản lý vừa trực tiếp đứng lớp.",
    steps: [
      "Xem các buổi học trong tuần của các lớp bạn dạy",
      "Bấm vào một buổi để mở chi tiết, điểm danh hoặc giao bài",
    ],
  },
  "/admin/my-attendance": {
    title: "Điểm danh lớp của bạn",
    summary: "Điểm danh 1 click ngay trong lớp.",
    steps: [
      "Chọn buổi học cần điểm danh",
      "Bấm vào tên từng học sinh để chuyển trạng thái: Có / Vắng / Trễ",
      "Lưu lại — chuyên cần được tự động tổng hợp vào báo cáo",
    ],
  },
  "/admin/my-assignments": {
    title: "Giao bài tập cho lớp bạn dạy",
    summary: "Giao bài, chấm điểm, theo dõi nộp bài.",
    steps: [
      "Bấm 'Giao bài tập mới', chọn lớp",
      "Điền tiêu đề, mô tả, hạn nộp; đính kèm file nếu cần",
      "Học sinh sẽ thấy bài trong cổng của họ và nộp lại để bạn chấm",
    ],
  },
  "/admin/reports": {
    title: "Báo cáo & Dashboard",
    summary: "Tổng quan real-time, báo cáo chi tiết theo tháng.",
    steps: [
      "Chọn khoảng thời gian muốn xem",
      "Xem các chỉ số: tỉ lệ điểm danh, học phí, lương, lớp đang hoạt động",
      "Bấm 'Xuất Excel' để tải báo cáo về máy (yêu cầu gói có hỗ trợ xuất file)",
    ],
  },
  "/admin/announcements": {
    title: "Thông báo cho trung tâm",
    summary: "Gửi hàng loạt cho phụ huynh, học viên, giáo viên — hẹn giờ và đính kèm file.",
    steps: [
      "Bấm 'Tạo thông báo'",
      "Chọn đối tượng nhận: toàn trung tâm, theo lớp, hoặc theo vai trò",
      "Soạn nội dung; đính kèm file nếu cần",
      "Gửi ngay hoặc hẹn giờ gửi sau",
    ],
  },
  "/admin/documents": {
    title: "Thư viện tài liệu",
    summary: "Chia sẻ tài liệu, đề thi, video bài giảng cho giáo viên và học sinh.",
    steps: [
      "Tạo danh mục theo môn hoặc theo lớp",
      "Upload file hoặc dán link (Google Drive, YouTube...)",
      "Chọn hiển thị cho portal nào: chỉ Giáo viên, chỉ Học sinh, hoặc cả hai",
    ],
  },
  "/billing": {
    title: "Gói thanh toán",
    summary: "Xem gói hiện tại, nâng cấp và thanh toán bằng mã QR.",
    steps: [
      "Xem hạn mức của gói hiện tại (lớp, học sinh, giáo viên)",
      "Bấm 'Nâng cấp' và chọn gói phù hợp",
      "Quét mã QR để chuyển khoản — hệ thống tự động kích hoạt sau khi xác nhận",
    ],
  },
  // ===== TEACHER =====
  "/teacher": {
    title: "Tổng quan ngày dạy",
    summary: "Xem nhanh công việc dạy học hôm nay.",
    steps: [
      "Kiểm tra danh sách buổi học trong ngày",
      "Bấm vào lớp để vào điểm danh hoặc giao bài",
    ],
  },
  "/teacher/schedule": {
    title: "Lịch dạy",
    summary: "Xem lịch dạy của bạn theo tuần.",
    steps: [
      "Chuyển tuần bằng nút mũi tên",
      "Bấm vào buổi học để mở chi tiết và các thao tác liên quan",
    ],
  },
  "/teacher/attendance": {
    title: "Điểm danh 1 click",
    summary: "Trên điện thoại, ngay trong lớp.",
    steps: [
      "Chọn lớp và buổi học cần điểm danh",
      "Bấm vào tên từng học sinh để đổi trạng thái: Có / Vắng / Trễ",
      "Lưu lại — chuyên cần tự cập nhật vào hồ sơ học sinh",
    ],
  },
  "/teacher/assignments": {
    title: "Giao bài tập",
    summary: "Giao bài, theo dõi nộp bài.",
    steps: [
      "Bấm 'Giao bài tập mới' và chọn lớp",
      "Điền tiêu đề, mô tả, hạn nộp; đính kèm file đề bài nếu cần",
      "Theo dõi danh sách học sinh đã nộp / chưa nộp",
    ],
  },
  "/teacher/grading": {
    title: "Chấm điểm & feedback",
    summary: "Chấm bài tập học sinh đã nộp và gửi nhận xét.",
    steps: [
      "Chọn bài tập cần chấm",
      "Mở từng bài nộp, cho điểm và viết feedback",
      "Lưu — học sinh sẽ thấy điểm và nhận xét ngay trong cổng của họ",
    ],
  },
  "/teacher/analytics": {
    title: "Thống kê lớp dạy",
    summary: "Xem tổng hợp về chuyên cần, kết quả bài tập của các lớp bạn phụ trách.",
    steps: [
      "Chọn lớp muốn xem",
      "Xem tỉ lệ điểm danh, điểm trung bình, học sinh cần chú ý",
    ],
  },
  "/teacher/documents": {
    title: "Tài liệu trung tâm",
    summary: "Xem và tải các tài liệu trung tâm chia sẻ cho giáo viên.",
    steps: [
      "Duyệt theo danh mục",
      "Bấm vào file/link để xem hoặc tải về",
    ],
  },
  "/teacher/announcements": {
    title: "Thông báo cho lớp bạn dạy",
    summary: "Chỉ gửi tới học sinh trong các lớp bạn phụ trách — không phải toàn trung tâm.",
    steps: [
      "Bấm 'Tạo thông báo'",
      "Chọn lớp muốn gửi (chỉ các lớp bạn dạy mới hiện ra)",
      "Soạn nội dung và gửi",
    ],
    tips: ["Thông báo cấp trung tâm do quản lý gửi từ portal Admin."],
  },
  // ===== STUDENT =====
  "/student": {
    title: "Trang chủ học sinh",
    summary: "Tổng quan lịch học hôm nay và bài tập sắp đến hạn.",
    steps: [
      "Xem các lớp có lịch hôm nay",
      "Kiểm tra bài tập sắp đến hạn nộp",
    ],
  },
  "/student/assignments": {
    title: "Bài tập của bạn",
    summary: "Xem và nộp bài tập do giáo viên giao.",
    steps: [
      "Mở bài tập để đọc đề và hạn nộp",
      "Upload bài làm (file) hoặc nhập nội dung trực tiếp",
      "Bấm 'Nộp bài' — bạn có thể xem điểm và feedback sau khi giáo viên chấm",
    ],
  },
  "/student/schedule": {
    title: "Lịch học",
    summary: "Xem lịch học theo tuần của tất cả lớp bạn đang tham gia.",
    steps: [
      "Chuyển tuần bằng nút mũi tên",
      "Bấm vào buổi học để xem chi tiết: phòng, giáo viên",
    ],
  },
  "/student/attendance": {
    title: "Lịch sử điểm danh",
    summary: "Xem lại trạng thái điểm danh của bạn ở từng buổi.",
    steps: [
      "Chọn lớp muốn xem",
      "Xem chi tiết các buổi: Có / Vắng / Trễ",
    ],
  },
  "/student/classes": {
    title: "Lớp đang học",
    summary: "Danh sách các lớp bạn đang tham gia.",
    steps: [
      "Bấm vào lớp để xem giáo viên, lịch học, tài liệu liên quan",
    ],
  },
  "/student/documents": {
    title: "Tài liệu trung tâm",
    summary: "Xem và tải các tài liệu trung tâm chia sẻ cho học sinh.",
    steps: [
      "Duyệt theo danh mục",
      "Bấm vào file/link để xem hoặc tải về",
    ],
  },
};

export function getAdminWelcomeTopic(isAdminTeacher: boolean): HelpTopic {
  if (isAdminTeacher) {
    return {
      title: "Chào mừng đến với Ezyclass!",
      summary: "Bạn vừa là người quản lý vừa là giáo viên với gói hiện tại.",
      steps: [
        "Vào 'Người dùng' để tạo tài khoản cho học sinh của bạn",
        "Vào 'Lớp học' để tạo lớp và thêm học sinh vào",
        "Dùng menu 'Lịch dạy', 'Điểm danh', 'Bài tập' để dạy và quản lý lớp của chính bạn",
      ],
      tips: ["Khi cần thêm giáo viên khác, nâng cấp lên gói Trung tâm ở mục 'Gói thanh toán'."],
    };
  }
  return {
    title: "Chào mừng đến với Ezyclass!",
    summary: "Hãy bắt đầu bằng cách thiết lập trung tâm của bạn.",
    steps: [
      "Vào 'Người dùng' để tạo tài khoản cho giáo viên và học sinh",
      "Vào 'Lớp học' để tạo lớp, xếp lịch và phân giáo viên phụ trách",
      "Thêm học sinh vào lớp tương ứng",
    ],
    tips: ["Mỗi giáo viên/học sinh sẽ có tài khoản riêng để đăng nhập vào cổng của họ."],
  };
}

/**
 * Tìm topic phù hợp nhất với route hiện tại theo longest-prefix match.
 * Trả về null nếu không có nội dung.
 */
export function findHelpTopicForPath(pathname: string): { key: string; topic: HelpTopic } | null {
  let best: { key: string; topic: HelpTopic } | null = null;
  for (const [key, topic] of Object.entries(HELP_CONTENT)) {
    if (pathname === key || pathname.startsWith(key + "/")) {
      if (!best || key.length > best.key.length) best = { key, topic };
    }
  }
  return best;
}
